import { useMemo } from 'react';
import { getImagePriorityRules, shouldUsePlaceholder } from '@/utils/imageOptimization';
import { measureImageOptimization } from '@/utils/performanceTracking';
import type { ImageContext } from '@/helpers/content/queries/types';

export const useOptimizedImage = (
  src: string,
  alt: string,
  originalWidth: number,
  originalHeight: number,
  context: ImageContext,
  priority: boolean = false,
  enablePerformanceTracking: boolean = false
) => {
  return useMemo(() => {
    if (!src) return null;
    
    const imageUrl = src.startsWith('//') ? `https:${src}` : src;
    
    // Get priority rules based on context (let Next.js handle sizing/quality)
    const priorityRules = getImagePriorityRules(context);
    const shouldPrioritize = priority || priorityRules.shouldPrioritize;
    const shouldUsePlaceholderBlur = shouldUsePlaceholder(originalWidth, originalHeight, context);
    
    // Optional performance tracking in development
    if (enablePerformanceTracking && process.env.NODE_ENV === 'development') {
      measureImageOptimization(
        { width: originalWidth, height: originalHeight },
        { width: originalWidth, height: originalHeight }, // No longer modifying dimensions
        context
      );
    }
    
    // Return only non-conflicting props - let Next.js handle optimization
    return {
      src: imageUrl,
      alt,
      // Remove conflicting props: width, height, sizes, quality
      priority: shouldPrioritize,
      placeholder: shouldUsePlaceholderBlur ? ('blur' as const) : undefined,
      blurDataURL: shouldUsePlaceholderBlur ? 
        'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q==' 
        : undefined,
      // Add context for debugging
      'data-image-context': context
    };
  }, [src, alt, originalWidth, originalHeight, context, priority, enablePerformanceTracking]);
}; 